import { useTranslation } from "react-i18next";
import { UserMinus } from "lucide-react";
import {
  Button,
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tabipla/ui";
import type { GroupRole, MemberType } from "@tabipla/shared";
import { getRoleBadgeStyle, canModifyMember } from "../../../utils/group-detail";

interface MemberCardProps {
  memberId: string;
  memberType: MemberType;
  memberName: string;
  memberEmail?: string | null;
  role: GroupRole;
  isOwner: boolean;
  onUpdateRole: (
    memberId: string,
    memberType: MemberType,
    role: Exclude<GroupRole, "owner">
  ) => void;
  onRemove: (memberId: string, memberType: MemberType) => void;
}

export function MemberCard({
  memberId,
  memberType,
  memberName,
  memberEmail,
  role,
  isOwner,
  onUpdateRole,
  onRemove,
}: MemberCardProps) {
  const { t } = useTranslation();

  return (
    <div className="rounded-xl border p-3 transition-colors hover:bg-accent/50">
      {/* Row 1: Name + Badge */}
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium">{memberName}</p>
          {memberEmail && <p className="truncate text-sm text-muted-foreground">{memberEmail}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {memberType === "guest" && (
            <span className="whitespace-nowrap rounded-full bg-yellow-100 px-2 py-0.5 text-xs text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300">
              {t("groups.guest")}
            </span>
          )}
          <Badge className={getRoleBadgeStyle(role)}>{t(`groups.roles.${role}`)}</Badge>
        </div>
      </div>

      {/* Row 2: Actions (owner only, for non-owner members) */}
      {isOwner && canModifyMember(role) && (
        <div className="mt-2 flex items-center justify-end gap-2 border-t pt-2">
          <Select
            value={role}
            onValueChange={(value) =>
              onUpdateRole(memberId, memberType, value as Exclude<GroupRole, "owner">)
            }
          >
            <SelectTrigger className="h-8 w-24 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="editor">{t("groups.roles.editor")}</SelectItem>
              <SelectItem value="viewer">{t("groups.roles.viewer")}</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-xs text-destructive hover:bg-destructive/10"
            onClick={() => onRemove(memberId, memberType)}
          >
            <UserMinus className="mr-1 h-4 w-4" />
            {t("groups.remove", "削除")}
          </Button>
        </div>
      )}
    </div>
  );
}
